// EdgeBuildPanel — Samsung-edge-panel-style build flyout.
// Specialized storage is NOT duplicated in a separate list: each dedicated
// storage item appears once as a nested slide-out under its most logical producer.

import React, { useEffect, useMemo, useState } from "react";
import { useGame } from "../game/em2c-store";
import { EM2C_BUILDING_LIST, EM2C_CATEGORIES } from "../game/data/em2c-buildings";
import BuildingCard, { isBuildingUnlocked } from "./BuildingCard";
import { ChevronRight, Package, X } from "lucide-react";

export default function EdgeBuildPanel({ open, category, onClose, onBuildingSelected }) {
  const startPlacement = useGame((s) => s.startPlacement);
  const placementType = useGame((s) => s.placementType);
  const canAfford = useGame((s) => s.canAfford);
  const research = useGame((s) => s.research);
  const [expandedParent, setExpandedParent] = useState(null);

  const completed = research?.completed || [];
  const catMeta = EM2C_CATEGORIES.find((c) => c.id === category);

  useEffect(() => {
    setExpandedParent(null);
  }, [category, open]);

  // Nested storage is intentionally excluded from top-level category cards.
  // Command Center remains visible so a colony can recover after losing HQ.
  const items = category
    ? EM2C_BUILDING_LIST.filter((b) => b.category === category && !b.nestedStorage)
    : [];

  const nestedChildren = useMemo(() => {
    if (!expandedParent) return [];
    return EM2C_BUILDING_LIST.filter((b) => b.nestedStorage && b.buildMenuParent === expandedParent);
  }, [expandedParent]);

  const expandedDef = expandedParent ? EM2C_BUILDING_LIST.find((b) => b.id === expandedParent) : null;

  const selectBuilding = (type) => {
    startPlacement(type);
    setExpandedParent(null);
    onBuildingSelected();
  };

  return (
    <div
      className="absolute left-14 top-0 bottom-0 z-20 pointer-events-none"
      style={{ fontFamily: "Rajdhani, sans-serif" }}
    >
      <div
        data-testid="edge-build-panel"
        className={`relative h-full w-[440px] max-w-[calc(100vw-3.5rem)] bg-[#0A0C10]/95 backdrop-blur-md border-r border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.6)] flex flex-col transition-[transform,opacity] duration-200 ease-out ${open ? "pointer-events-auto" : "pointer-events-none"}`}
        style={{
          transform: open ? "translateX(0)" : "translateX(calc(-100% - 120px))",
          opacity: open ? 1 : 0,
        }}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Build Category</div>
            <div className="text-sm font-semibold uppercase tracking-wide text-cyan-400">
              {catMeta?.label || ""}
            </div>
          </div>
          <button
            data-testid="btn-close-build-panel"
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 border border-white/15 text-slate-400 hover:text-white hover:border-white/30"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 flex flex-col gap-2" style={{ scrollbarWidth: "thin" }}>
          {items.length === 0 && (
            <div className="text-slate-500 text-xs px-2 py-6 font-mono">No buildings in this category yet</div>
          )}

          {items.map((b) => {
            const afford = canAfford(b.id);
            const active = placementType === b.id;
            const unlocked = isBuildingUnlocked(b, completed);
            const children = EM2C_BUILDING_LIST.filter((child) => child.nestedStorage && child.buildMenuParent === b.id);
            const childOpen = expandedParent === b.id;

            return (
              <div key={b.id} className="border border-transparent">
                <BuildingCard
                  b={b}
                  afford={afford}
                  active={active}
                  unlocked={unlocked}
                  onSelect={selectBuilding}
                />

                {children.length > 0 && (
                  <div className="mt-1">
                    <button
                      data-testid={`storage-slide-${b.id}`}
                      onClick={() => setExpandedParent(childOpen ? null : b.id)}
                      aria-expanded={childOpen}
                      className={`w-full px-3 py-2 flex items-center gap-2 border text-[10px] font-mono uppercase tracking-widest transition-colors ${childOpen ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-300" : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-cyan-400/40"}`}
                    >
                      <Package size={13} />
                      <span className="flex-1 text-left">{children.map(child => child.name).join(" / ")}</span>
                      <ChevronRight size={13} className={childOpen ? "rotate-90" : ""} />
                    </button>
                    {childOpen && (
                      <div data-testid="nested-storage-panel" className="p-2 border border-cyan-400/25 bg-cyan-400/[0.04] flex flex-col gap-2">
                        {children.map(child => (
                          <BuildingCard
                            key={child.id}
                            b={child}
                            afford={canAfford(child.id)}
                            active={placementType === child.id}
                            unlocked={isBuildingUnlocked(child, completed)}
                            onSelect={selectBuilding}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>


      </div>
    </div>
  );
}
